import { useTranslation } from "react-i18next";
import { useFormContext, Controller } from "react-hook-form";
import {
  Box,
  FormLabel,
  TextField,
  FormControl,
  Typography,
  MenuItem,
  Select,
  Divider,
  FormHelperText,
} from "@mui/material";

import { currencyList } from "../../../../../../enums/currencies.enum";

import { CreateProgramFormData } from "../../../../types/service.type";
import { FinalServiceCostIcon, ServiceAmountWarningIcon } from "../../../../assets/icons/create-service-icons";
import { useMinimumServicePrices } from "../../../../../../api/queries/payment/paymentQueries";

const DetailsAndPricing = () => {
  const { t } = useTranslation("goService", { keyPrefix: "createServicePage.detailsAndPricingStep" });
  const { t: tCurrency } = useTranslation("common", { keyPrefix: "currency" });

  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<CreateProgramFormData>();

  const currency = watch("paymentConfig.currency");
  const amount = watch("paymentConfig.amount");
  const amountNumber = Number(amount);

  const { minimumServicePrices } = useMinimumServicePrices();
  const minimumServicePrice = minimumServicePrices?.[currency as keyof typeof minimumServicePrices];

  const currencyName = tCurrency(currency);

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.formLabel}>{t("programDetails")}</Typography>
      <Controller
        name="numberOfVideos"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth component="fieldset" error={!!errors.numberOfVideos}>
            <FormLabel error={!!errors.numberOfVideos}>{t("numberOfVideosInTheProgram")}</FormLabel>
            <TextField
              type="number"
              {...field}
              fullWidth
              placeholder={t("enterNumberOfVideos")}
              error={!!errors.numberOfVideos}
            />
            {errors.numberOfVideos && <FormHelperText error>{t(errors.numberOfVideos.message)}</FormHelperText>}
          </FormControl>
        )}
      />
      <Controller
        name="idealCompletionTime"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth component="fieldset" error={!!errors.idealCompletionTime}>
            <FormLabel sx={styles.formChildLabel} error={!!errors.idealCompletionTime}>
              {t("programIdeallyShouldBeFinishedIn")}
            </FormLabel>
            <TextField
              type="number"
              {...field}
              fullWidth
              placeholder={t("enterNumber")}
              error={!!errors.idealCompletionTime}
            />
            {errors.idealCompletionTime && (
              <FormHelperText error>{t(errors.idealCompletionTime.message)}</FormHelperText>
            )}
          </FormControl>
        )}
      />

      <Divider />

      <Box sx={styles.fixedCostCard}>
        <Box sx={styles.fixedCostCardTitleWrapper}>
          <Typography sx={styles.fixedCostTitle}>{t("fixedCost")}</Typography>
          <Typography sx={styles.fixedCostDescription}>{t("setAFixedAndTotalPriceForYourRequest")}</Typography>
        </Box>
        <Box sx={styles.serviceAmountWarningBox}>
          <ServiceAmountWarningIcon />
          <Typography sx={styles.fixedCostWarning}>
            {t("serviceAmountWarning", { currency: currencyName, amount: minimumServicePrice })}
          </Typography>
        </Box>
        <Controller
          name="paymentConfig.amount"
          control={control}
          render={({ field: amountField }) => (
            <>
              <FormLabel sx={styles.formChildLabel} error={!!errors.paymentConfig?.amount}>
                {t("totalPrice")}
              </FormLabel>
              <Box sx={styles.currencySelectSubMenuContainer}>
                <Controller
                  name="paymentConfig.currency"
                  control={control}
                  render={({ field: currencyField }) => (
                    <Select
                      {...currencyField}
                      value={currencyField.value}
                      sx={styles.currencySelectSubMenu}
                      onChange={(e) => currencyField.onChange(e.target.value)}
                    >
                      {currencyList.map((currency: string) => {
                        return (
                          <MenuItem key={currency} value={currency}>
                            {currency}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  )}
                />
                <TextField
                  {...amountField}
                  fullWidth
                  placeholder={t("enterTotalPrice")}
                  sx={styles.averageCostTextField}
                />
              </Box>
              {errors.paymentConfig?.amount && (
                <FormHelperText error>
                  {t(errors.paymentConfig?.amount?.message, { amount: minimumServicePrice })}
                </FormHelperText>
              )}
            </>
          )}
        />
      </Box>
      <Box sx={styles.finalServiceCostContainer}>
        <FinalServiceCostIcon />
        <Box sx={styles.finalServiceCostBox}>
          <Typography sx={styles.finalServiceCostTitle}>{t("yourFinalProgramCost")}</Typography>
          <Typography sx={styles.finalServiceCostDescription}>{t("goalngoServiceAreNonRefundable")}</Typography>
        </Box>
        <Box sx={styles.finalServiceCostRow}>
          <Typography sx={styles.serviceCostTitle}>{t("programPriceForUsers")}</Typography>
          <Typography sx={styles.finalServiceCostValue}>
            {currency} {Number.isFinite(amountNumber) ? amountNumber.toFixed(2) : "00.00"}
          </Typography>
        </Box>
        <Box sx={styles.finalServiceCostRow}>
          <Typography sx={styles.serviceCostTitle}>{t("fees10")}</Typography>
          <Typography sx={styles.finalServiceCostValue}>
            {currency} {Number.isFinite(amountNumber) ? (amountNumber * 0.1).toFixed(2) : "00.00"}
          </Typography>
        </Box>
        <Divider />
        <Box sx={styles.finalServiceCostRow}>
          <Typography sx={styles.serviceCostTitle}>{t("totalPrice")}</Typography>
          <Typography sx={styles.finalServiceCostValue}>
            {currency} {Number.isFinite(amountNumber) ? (amountNumber * 0.9).toFixed(2) : "00.00"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default DetailsAndPricing;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  serviceDeliveryRadioGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  formLabel: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  fixedCostCard: {
    flex: 1,
    border: "1px solid var(--Gray-200, #E4E7EC)",
    borderRadius: "12px",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  fixedCostContainer: {
    display: "flex",
    flexDirection: " column",
    alignItems: "flex-start",
    gap: "24px",
  },
  fixedCostTitle: {
    fontFamily: "Montserrat",
    color: "var(--Gray-700, #344054)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
  fixedCostDescription: {
    fontFamily: "Montserrat",
    color: "var(--Gray-600, #475467)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "24px",
  },
  formControlLabel: {
    margin: 0,
    gap: "12px",
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
  formChildLabel: {
    color: "var(--Dark---800, #414141)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  finalServiceCostContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    borderRadius: "12px",
    border: "1px solid #EAECF0",
    background: "var(--Green---25, #E7EFED)",
    padding: "16px",
  },
  finalServiceCostBox: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  finalServiceCostRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  finalServiceCostTitle: {
    fontFamily: "Montserrat",
    color: "var(--Gray-900, #1A1C21)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  finalServiceCostDescription: {
    fontFamily: "Montserrat",
    color: "var(--Dark---600, #707070)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
  },
  serviceCostTitle: {
    fontFamily: "Montserrat",
    color: "var(--Gray-900, #1A1C21)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",
  },
  finalServiceCostValue: {
    fontFamily: "Montserrat",
    color: "var(--Gray-900, #1A1C21)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  fixedCostCardTitleWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  serviceAmountWarningBox: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    borderRadius: "8px",
    background: "var(--Brand-50, #E7FFE4)",
    padding: "6px 8px",
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
  },
  fixedCostWarning: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
  },
  currencySelectSubMenu: {
    borderTopRightRadius: "0 !important",
    borderBottomRightRadius: "0 !important",
    borderTop: "none !important",
    borderBottom: "none !important",
    borderLeft: "none !important",
    borderRight: "1px solid var(--Gray-300, #D0D5DD)",
    paddingRight: "0",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none !important",
    },
    "& .MuiInputBase-root.MuiOutlinedInput-root": {
      border: "none !important",
    },
    "& .MuiSelect-select.MuiSelect-outlined.MuiInputBase-input": {
      padding: "8px 16px 8px 8px",
    },
  },
  currencySelectSubMenuContainer: {
    display: "flex",
    border: "1px solid var(--Gray-300, #D0D5DD)",
    borderRadius: "33px",
    width: "100%",
  },
  averageCostTextField: {
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none !important",
    },
    "& .MuiInputBase-root": {
      border: "none !important",
    },
  },
  fixedCostSubContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  fixedCostLabel: {
    color: "var(--Gray-700, #344054)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",
  },
};
